// Print Kit — apparel, price estimates, the order timeline and reorder.
//
// The apparel catalog is web/public/print/apparel.json, the same file the
// service validates every apparel line against (server/src/printorders.js):
// garments, colors, logo placements, sizes, minimums and the garment shapes
// the mockup draws. Prices are never here — they come from the price book the
// print desk keeps (GET /print/prices/:ws), and the service prices the order
// again when it's placed. The mockup and the validation below mirror the
// server's so what the contractor sees is what the supplier gets.
import { PRINT_BASE } from "./printkit";

export interface GarmentColor { id: string; name: string; hex: string }
export interface GarmentPlacement { id: string; label: string; locations: { view: string; box: number[]; area?: string }[] }
export interface GarmentView { viewBox: string; outline: string; trim?: string[]; details?: string[]; bands?: string[] }
export interface Garment {
  id: string; name: string; blurb?: string; blank?: string; supplierProduct?: string; decoration?: string;
  min: number; tiers: number[]; sizes: string[]; youth: boolean; defaultProvider?: string;
  colors: GarmentColor[]; placements: GarmentPlacement[]; views: Record<string, GarmentView>;
}
export interface ApparelCatalog {
  version: number; sizes: string[]; sizeLabels: Record<string, string>; youthSizes: string[]; upchargeSizes: string[]; maxPerLine: number; garments: Garment[];
}
/** one apparel line in the cart (settings.printKit.apparel) — logo: this line's own artwork, else the brand logo */
export interface ApparelLine { id: string; garmentId: string; colorId: string; placementId: string; sizes: Record<string, number>; logo?: string | null }
/** a price-book row as the contractor may see it (no costs, no suppliers) */
export interface PriceRow { templateId: string; quantity: number; priceCents: number; minQty?: number | null; upchargeCents?: number; extraLocationCents?: number }

let catalogPromise: Promise<ApparelCatalog> | null = null;
export function loadApparelCatalog(): Promise<ApparelCatalog> {
  if (catalogPromise) return catalogPromise;
  catalogPromise = fetch(PRINT_BASE + "apparel.json").then((r) => {
    if (!r.ok) throw new Error("Couldn't load " + PRINT_BASE + "apparel.json");
    return r.json();
  }).catch((e) => { catalogPromise = null; throw e; });
  return catalogPromise;
}
export function garmentOf(cat: ApparelCatalog | null, id: string): Garment | null {
  return ((cat && cat.garments) || []).find((g) => g.id === id) || null;
}
export function lineQty(sizes: Record<string, any> | null | undefined): number {
  let n = 0;
  for (const k in sizes || {}) { const v = Number((sizes as any)[k]); if (Number.isFinite(v) && v > 0) n += Math.floor(v); }
  return n;
}
/** { S: 4, M: 10 } → "S 4 · M 10", in catalog order */
export function sizeSummary(sizes: Record<string, any> | null | undefined, order?: string[]): string {
  const keys = Object.keys(sizes || {}).filter((k) => Number((sizes as any)[k]) > 0);
  if (order) keys.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return keys.map((k) => k + " " + Number((sizes as any)[k])).join(" · ");
}
/** the sizes a garment offers, youth first when it has them */
export function garmentSizes(cat: ApparelCatalog, g: Garment): string[] {
  return [...(g.youth ? cat.youthSizes || [] : []), ...g.sizes];
}

/* ---- the mockup: the same drawing as server/src/printorders.js mockupSvg ---- */
export function shade(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  const n = m ? parseInt(m[1], 16) : 0x888888;
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(amt < 0 ? c * (1 + amt) : c + (255 - c) * amt)));
  return "#" + [n >> 16, (n >> 8) & 255, n & 255].map((c) => f(c).toString(16).padStart(2, "0")).join("");
}
export function isLight(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  const n = m ? parseInt(m[1], 16) : 0x888888;
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.6;
}
const escAttr = (s: any) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as any)[c]);
export function mockupSvg(garment: Garment, opts: { colorHex?: string; placementId?: string; logo?: string | null; view?: string; idPrefix?: string } = {}): string {
  const view = opts.view || "front";
  const v = garment && garment.views && garment.views[view];
  if (!v) return "";
  const fill = opts.colorHex || "#888888";
  const light = isLight(fill);
  const edge = light ? shade(fill, -0.32) : shade(fill, -0.45);
  const seam = light ? shade(fill, -0.16) : shade(fill, 0.16);
  const trim = light ? shade(fill, -0.08) : shade(fill, -0.22);
  const placement = (garment.placements || []).find((p) => p.id === opts.placementId) || null;
  const boxes = placement ? placement.locations.filter((l) => l.view === view).map((l) => l.box) : [];
  const id = String(opts.idPrefix || "m").replace(/[^a-z0-9-]/gi, "");
  const logos = boxes.map(([x, y, w, h]) => opts.logo
    ? `<image href="${escAttr(opts.logo)}" x="${x}" y="${y}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet"/>`
    : `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="4" fill="none" stroke="${light ? "#00000055" : "#ffffff77"}" stroke-width="2" stroke-dasharray="6 5"/>`).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${escAttr(v.viewBox)}" role="img" aria-label="${escAttr(garment.name + " — " + view)}">`
    + `<defs><linearGradient id="${id}-sh" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".10"/><stop offset=".55" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".14"/></linearGradient>`
    + `<linearGradient id="${id}-bd" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#E9ECEF"/><stop offset="1" stop-color="#AEB3BA"/></linearGradient></defs>`
    + `<path d="${escAttr(v.outline)}" fill="${fill}" stroke="${edge}" stroke-width="2.5" stroke-linejoin="round"/>`
    + (v.trim || []).map((d) => `<path d="${escAttr(d)}" fill="${trim}" stroke="${edge}" stroke-width="1.5" stroke-linejoin="round"/>`).join("")
    + (v.bands || []).map((d) => `<path d="${escAttr(d)}" fill="url(#${id}-bd)" stroke="#8F959C" stroke-width="1"/>`).join("")
    + (v.details || []).map((d) => `<path d="${escAttr(d)}" fill="none" stroke="${seam}" stroke-width="2" stroke-linecap="round"/>`).join("")
    + `<path d="${escAttr(v.outline)}" fill="url(#${id}-sh)" pointer-events="none"/>`
    + logos + `</svg>`;
}
/** the views a line's logo appears on, front first */
export function mockupViews(garment: Garment, placementId: string): string[] {
  const p = (garment.placements || []).find((x) => x.id === placementId);
  const views = p ? Array.from(new Set(p.locations.map((l) => l.view))) : ["front"];
  return views.sort((a, b) => (a === "front" ? -1 : b === "front" ? 1 : 0));
}

/* ---- prices: estimates from the price book (the service prices it again) ---- */
export function apparelTier(prices: PriceRow[], templateId: string, qty: number): PriceRow | null {
  let hit: PriceRow | null = null;
  for (const r of (prices || []).filter((p) => p.templateId === templateId).sort((a, b) => a.quantity - b.quantity)) if (r.quantity <= qty) hit = r;
  return hit;
}
export function apparelMin(g: Garment, prices: PriceRow[]): number {
  const r = (prices || []).find((p) => p.templateId === "apparel:" + g.id && Number(p.minQty) > 0);
  return r ? Number(r.minQty) : Number(g.min) || 1;
}
/**
 * What an apparel line comes to at the price book's break it reaches: per
 * piece, the total, and the next break (so "12 more and it's $2 less each").
 * null when the garment has no price yet (the print desk quotes it).
 */
export function apparelEstimate(line: ApparelLine, g: Garment, cat: ApparelCatalog, prices: PriceRow[]) {
  const qty = lineQty(line.sizes);
  const tid = "apparel:" + g.id;
  const tier = apparelTier(prices, tid, qty);
  const rows = (prices || []).filter((p) => p.templateId === tid).sort((a, b) => a.quantity - b.quantity);
  const placement = g.placements.find((p) => p.id === line.placementId);
  const extraLocs = Math.max(0, ((placement && placement.locations.length) || 1) - 1);
  const up = (cat.upchargeSizes || []).reduce((s, k) => s + (Number(line.sizes && line.sizes[k]) || 0), 0);
  const unitAt = (r: PriceRow) => r.priceCents + extraLocs * Number(r.extraLocationCents || 0);
  const next = rows.find((r) => r.quantity > qty) || null;
  if (!tier) return { qty, unitCents: null, totalCents: null, upchargeCents: 0, from: rows.length ? unitAt(rows[0]) : null, fromQty: rows.length ? rows[0].quantity : null, next: null as null | { quantity: number; unitCents: number } };
  const unit = unitAt(tier);
  const upcharge = up * Number(tier.upchargeCents || 0);
  return { qty, unitCents: unit, totalCents: unit * qty + upcharge, upchargeCents: upcharge, from: null, fromQty: null, next: next ? { quantity: next.quantity, unitCents: unitAt(next) } : null };
}
/** a print piece's price at its tier, or null (priced by the print desk) */
export function printEstimate(prices: PriceRow[], templateId: string, qty: number): number | null {
  const r = (prices || []).find((p) => p.templateId === templateId && p.quantity === qty);
  return r ? r.priceCents : null;
}

/* ---- validation: the same rules and wording as the service ---- */
export function validateApparelLine(line: ApparelLine, cat: ApparelCatalog | null, prices: PriceRow[] = []): string[] {
  if (!cat) return ["Apparel isn't available right now."];
  const g = garmentOf(cat, line && line.garmentId);
  if (!g) return ["Pick a garment."];
  const errors: string[] = [];
  if (!g.colors.some((c) => c.id === line.colorId)) errors.push(g.name + ": pick one of the colors offered.");
  if (!g.placements.some((p) => p.id === line.placementId)) errors.push(g.name + ": pick where the logo goes.");
  const allowed = garmentSizes(cat, g);
  for (const k in line.sizes || {}) {
    const n = Number(line.sizes[k]);
    if (!n) continue;
    if (!allowed.includes(k)) errors.push(g.name + ": " + ((cat.youthSizes || []).includes(k) ? "youth sizes aren't" : "size " + k + " isn't") + " offered.");
    else if (!Number.isInteger(n) || n < 0) errors.push(g.name + ": size " + k + " needs a whole number.");
  }
  const qty = lineQty(line.sizes), min = apparelMin(g, prices), max = cat.maxPerLine || 2500;
  if (!qty) errors.push(g.name + ": add how many of each size.");
  else if (qty < min) errors.push(g.name + ": " + min + " minimum — this line has " + qty + ".");
  else if (qty > max) errors.push(g.name + ": " + max.toLocaleString("en-US") + " per line at most — split it into two lines.");
  return errors;
}
/** what POST /print/order receives for an apparel line */
export function apparelOrderItem(line: ApparelLine, g: Garment, logo: string | null) {
  const sizes: Record<string, number> = {};
  for (const k in line.sizes || {}) if (Number(line.sizes[k]) > 0) sizes[k] = Math.floor(Number(line.sizes[k]));
  return { kind: "apparel", garmentId: g.id, name: g.name, colorId: line.colorId, placementId: line.placementId, sizes, quantity: lineQty(sizes), logo: logo || null };
}

/* ---- the contractor's order timeline ---- */
export const TIMELINE = [
  { key: "placed", label: "Placed", at: "createdAt" },
  { key: "paid", label: "Paid", at: "paidAt" },
  { key: "submitted", label: "Sent to printer", at: "submittedAt" },
  { key: "shipped", label: "Shipped", at: "shippedAt" },
  { key: "delivered", label: "Delivered", at: "deliveredAt" },
];
const RANK: Record<string, number> = { quote_requested: 0, pending_payment: 0, paid: 1, submitted: 2, in_production: 2, shipped: 3, delivered: 4 };
export interface TimelineStep { key: string; label: string; at: string | null; state: "done" | "next" | "todo"; detail?: string; link?: string | null }
/**
 * Placed → Paid → Sent to printer → Shipped (carrier + tracking link) →
 * Delivered. A step is done when the order's status is past it (an owner can
 * move a status by hand, so a step may be done without a date); the first
 * step not done is "next". Cancelled / refunded end the line where it stopped.
 */
export function orderTimeline(o: any): TimelineStep[] {
  const status = String((o && o.status) || "");
  const ended = status === "cancelled" || status === "refunded";
  let rank = RANK[status] != null ? RANK[status] : 0;
  if (ended) rank = TIMELINE.reduce((r, s, i) => (o[s.at] ? i : r), 0);
  const steps: TimelineStep[] = TIMELINE.map((s, i) => {
    const at = (o && o[s.at]) || null;
    const state: TimelineStep["state"] = i <= rank || at ? "done" : i === rank + 1 && !ended ? "next" : "todo";
    const step: TimelineStep = { key: s.key, label: s.label, at, state };
    if (s.key === "paid" && state !== "done") step.detail = status === "quote_requested" ? "Waiting on a price — we'll email you a pay link." : "Ready to pay.";
    if (s.key === "submitted" && status === "in_production") step.detail = "Proof approved — printing.";
    if (s.key === "shipped" && (o.tracking || o.carrier)) { step.detail = [o.carrier, o.tracking].filter(Boolean).join(" "); step.link = o.trackingUrl || null; }
    return step;
  });
  if (!ended) return steps;
  return [...steps.slice(0, rank + 1), { key: status, label: status === "refunded" ? "Refunded" : "Cancelled", at: null, state: "done" }];
}

/* ---- Reorder: an old order's lines back into the cart ---- */
export function reorderInto(order: any, opts: { templates: { id: string; name: string; product: { qty: number[] } }[]; catalog: ApparelCatalog | null; kit: any; newId?: () => string }) {
  const cart: Record<string, number> = { ...((opts.kit && opts.kit.cart) || {}) };
  const apparel: ApparelLine[] = [...((opts.kit && opts.kit.apparel) || [])];
  const copied: string[] = [], skipped: string[] = [];
  let n = 0;
  const newId = opts.newId || (() => "ap" + Date.now().toString(36) + (n++).toString(36));
  for (const it of (order && order.items) || []) {
    if (it.kind === "apparel" || String(it.templateId || "").startsWith("apparel:")) {
      const g = garmentOf(opts.catalog, it.garmentId);
      if (!g || !opts.catalog || !g.colors.some((c) => c.id === it.colorId) || !g.placements.some((p) => p.id === it.placementId)) { skipped.push(it.name || "An apparel line"); continue; }
      const allowed = garmentSizes(opts.catalog, g);
      const sizes: Record<string, number> = {};
      for (const k in it.sizes || {}) if (allowed.includes(k) && Number(it.sizes[k]) > 0) sizes[k] = Number(it.sizes[k]);
      if (!lineQty(sizes)) { skipped.push(g.name); continue; }
      apparel.push({ id: newId(), garmentId: g.id, colorId: it.colorId, placementId: it.placementId, sizes });
      copied.push(g.name);
      continue;
    }
    const t = (opts.templates || []).find((x) => x.id === it.templateId);
    if (!t) { skipped.push(it.name || it.templateId); continue; }
    const want = Number(it.quantity) || t.product.qty[0];
    // the same tier if it's still offered, else the nearest one
    const qty = t.product.qty.includes(want) ? want : t.product.qty.reduce((best, q) => (Math.abs(q - want) < Math.abs(best - want) ? q : best), t.product.qty[0]);
    cart[t.id] = qty;
    copied.push(t.name);
  }
  return { cart, apparel, copied, skipped };
}

/* ---- the receipt (opened in its own tab to print or save) ---- */
export function receiptHtml(o: any, opts: { company?: string; money: (c: number | null | undefined) => string }): string {
  const e = escAttr, money = opts.money;
  const date = (v: any) => (v ? new Date(v).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");
  const rows = (o.items || []).map((it: any) => {
    const what = it.kind === "apparel" || String(it.templateId || "").startsWith("apparel:")
      ? `${e(it.name)} — ${e(it.colorName || "")}, ${e(String(it.placementLabel || "").toLowerCase())}<br><small>${e(sizeSummary(it.sizes))}</small>`
      : `${e(it.name)}<br><small>${e(it.product || "")}</small>`;
    return `<tr><td>${what}</td><td class="n">${Number(it.quantity).toLocaleString("en-US")}</td><td class="n">${e(money(it.priceCents))}</td></tr>`;
  }).join("");
  const line = (label: string, c: any) => (c ? `<tr><td colspan="2" class="n">${label}</td><td class="n">${e(money(c))}</td></tr>` : "");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${e(o.id)}</title><meta name="viewport" content="width=device-width,initial-scale=1">
<style>body{font:14px/1.5 system-ui,-apple-system,Segoe UI,sans-serif;color:#15171C;max-width:640px;margin:32px auto;padding:0 16px}h1{font-size:22px;margin:0}table{width:100%;border-collapse:collapse;margin:18px 0}td{padding:8px 0;border-bottom:1px solid #eee;vertical-align:top}td.n{text-align:right;white-space:nowrap;padding-left:12px}small{color:#666}.muted{color:#666}.tot td{border:0;font-weight:800;font-size:16px}button{font:inherit;padding:8px 14px;border-radius:8px;border:1px solid #ccc;background:#fff;cursor:pointer}@media print{button{display:none}}</style></head><body>
<h1>Receipt</h1><p class="muted">Fence Works print order <b>${e(o.id)}</b><br>${e(opts.company || (o.contact && o.contact.company) || "")}</p>
<p>Placed ${e(date(o.createdAt))}${o.paidAt ? ` · Paid ${e(date(o.paidAt))}` : " · Not paid yet"}${o.paymentRef ? ` · Card payment ${e(o.paymentRef)}` : ""}</p>
<table><tr><td class="muted">Item</td><td class="n muted">Qty</td><td class="n muted">Price</td></tr>${rows}
${line("Subtotal", o.subtotalCents)}${line("Shipping", o.shippingCents)}${line("Tax", o.taxCents)}
<tr class="tot"><td colspan="2" class="n">${o.paidAt ? "Paid" : "Total"}</td><td class="n">${e(money(o.totalCents))}</td></tr></table>
${o.contact && o.contact.shipTo ? `<p class="muted">Ship to: ${e(o.contact.shipTo)}</p>` : ""}
<button onclick="window.print()">Print</button></body></html>`;
}
